<script setup lang="ts">
import ArtifactPicker from '@/components/artifacts/ArtifactPicker.vue'
import { Ref, ref } from 'vue'
import { SerializedChartType } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'
import ChartCreatorDialog from '@/components/charts/chart-creator/ChartCreatorDialog.vue'

const props = defineProps<{
  editor: import('@tiptap/vue-3').Editor
}>()

const addChartDialog: Ref<boolean> = ref(false)
const showSpecialNodeMenu = ref(false)
const addArtifactDialog = ref(false)
const newChartSettings: Ref<SerializedChartType | null> = ref(null)
const artifactIdToAdd: Ref<string | undefined> = ref(undefined)

function addArtifact() {
  addArtifactDialog.value = false
  props.editor
    .chain()
    .focus()
    .insertContent([
      {
        type: 'inline-artifact',
        attrs: {
          artifactId: artifactIdToAdd.value,
        },
      },
      {
        type: 'text',
        text: ' ',
      },
    ])
    .run()
}

function addChart() {
  props.editor
    .chain()
    .focus()
    .insertContent([
      {
        type: 'chart-block',
        attrs: {
          serialized: newChartSettings.value,
        },
      },
      {
        type: 'paragraph',
      },
    ])
    .run()
  addChartDialog.value = false
  newChartSettings.value = null
}
</script>

<template>
  <v-btn
    variant="text"
    :disabled="!editor.can().insertContent({ type: 'inline-artifact' }) && !editor.can().insertContent({ type: 'chart-block' })"
    class="rounded-0"
    icon
  >
    <v-icon icon="mdi-paperclip-plus" />
    <v-menu
      v-model="showSpecialNodeMenu"
      activator="parent"
      :close-on-content-click="true"
    >
      <v-list density="compact">
        <v-list-item
          prepend-icon="mdi-image-area"
          slim
          :disabled="!editor.can().insertContent({ type: 'inline-artifact' })"
          :title="$t('wysiwyg.add_artifact')"
          @click="addArtifactDialog = true"
        />
        <v-divider class="mx-2" />
        <v-list-item
          prepend-icon="mdi-chart-line"
          slim
          :disabled="!editor.can().insertContent({ type: 'chart-block' })"
          :title="$t('wysiwyg.add_chart')"
          @click="addChartDialog = true"
        />
      </v-list>
    </v-menu>
  </v-btn>

  <ChartCreatorDialog
    v-model:chart="newChartSettings"
    v-model:show-chart-dialog="addChartDialog"
    :is-edit-mode="false"
    hide-actions
    @save="addChart"
  />

  <GenericCustomDialog
    v-model="addArtifactDialog"
    max-width="auto"
  >
    <template #title> {{ $t('artifact_list.add_artifact') }} </template>
    <template #body>
      <ArtifactPicker v-model="artifactIdToAdd"></ArtifactPicker>
    </template>
    <template #actions>
      <v-row class="justify-end">
        <v-col cols="auto">
          <v-btn
            variant="flat"
            color="success"
            :disabled="!artifactIdToAdd"
            @click="addArtifact"
          >
            {{ $t('general_interface.buttons.add') }}
          </v-btn>
        </v-col>
      </v-row>
    </template>
  </GenericCustomDialog>
</template>
